import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  AlertCircle,
  ArrowRight,
  Calendar,
  CheckCircle2,
  Clock3,
  IndianRupee,
  Loader2,
  Plus,
  Search,
  Trophy,
  Users,
} from 'lucide-react';
import DashboardLayout from '../../components/DashboardLayout';
import { useAuth } from '../../context/AuthContext';
import { MODULES } from '../../constants/modules';
import { Button, Card, EmptyState, InfoBanner, StatusPill } from '../../components/ui';
import { getTournamentHome } from './tournamentApi';

const formatDate = (value) => value
  ? new Date(`${String(value).slice(0, 10)}T00:00:00`).toLocaleDateString('en-IN', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  })
  : 'Not set';

const statusPill = (status) => ({
  status: status === 'ongoing' ? 'confirmed' : status === 'upcoming' ? 'pending' : status,
  label: status ? `${status[0].toUpperCase()}${status.slice(1)}` : 'Draft',
});

export default function TournamentHome() {
  const { hasPermission } = useAuth();
  const canWrite = hasPermission(MODULES.TOURNAMENTS, 'write');
  const [home, setHome] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('all');
  const [sort, setSort] = useState('newest');

  useEffect(() => {
    let active = true;
    setLoading(true);
    getTournamentHome()
      .then((payload) => {
        if (active) setHome(payload.data);
      })
      .catch((err) => {
        if (active) setError(err.message || 'Failed to load tournaments');
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, []);

  const tournaments = useMemo(() => {
    const query = search.trim().toLowerCase();
    return [...(home?.tournaments || [])]
      .filter((tournament) => (
        (!query
          || tournament.name.toLowerCase().includes(query)
          || (tournament.facility_name || '').toLowerCase().includes(query))
        && (status === 'all' || tournament.lifecycle_status === status)
      ))
      .sort((a, b) => {
        if (sort === 'name') return a.name.localeCompare(b.name);
        if (sort === 'start') {
          return String(a.tournament_starts_at || '9999').localeCompare(
            String(b.tournament_starts_at || '9999')
          );
        }
        return new Date(b.created_at) - new Date(a.created_at);
      });
  }, [home, search, sort, status]);

  const summaryCards = [
    { label: 'Total', value: home?.summary?.total || 0, icon: Trophy },
    { label: 'Ongoing', value: home?.summary?.ongoing || 0, icon: Clock3 },
    { label: 'Upcoming', value: home?.summary?.upcoming || 0, icon: Calendar },
    { label: 'Completed', value: home?.summary?.completed || 0, icon: CheckCircle2 },
  ];

  return (
    <DashboardLayout>
      <div className="mx-auto max-w-6xl px-4 py-6">
        <div className="mb-6 flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="flex items-center gap-2 text-xl font-bold text-ga-text-primary">
              <Trophy className="h-6 w-6 text-ga-primary" />
              Tournaments
            </h1>
            <p className="mt-1 text-sm text-ga-text-muted">
              Track tournaments, registrations, and team approvals.
            </p>
          </div>
          {canWrite && (
            <Button as={Link} to="/dashboard/tournaments/new">
              <Plus className="h-4 w-4" /> Register Tournament
            </Button>
          )}
        </div>

        {error && <InfoBanner variant="danger" className="mb-4">{error}</InfoBanner>}

        {loading ? (
          <div className="py-16 text-center text-sm text-ga-text-muted">
            <Loader2 className="mx-auto mb-2 h-7 w-7 animate-spin" />
            Loading tournament dashboard…
          </div>
        ) : (
          <>
            <div className="mb-6 grid grid-cols-2 gap-3 lg:grid-cols-4">
              {summaryCards.map(({ label, value, icon: Icon }) => (
                <Card key={label} className="flex items-center justify-between p-4">
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wide text-ga-text-muted">{label}</p>
                    <p className="mt-1 text-2xl font-bold text-ga-text-primary">{value}</p>
                  </div>
                  <span className="rounded-ga-md bg-ga-primary-soft p-2.5 text-ga-primary">
                    <Icon className="h-5 w-5" />
                  </span>
                </Card>
              ))}
            </div>

            <div className="mb-6 grid min-w-0 gap-4 lg:grid-cols-3">
              <Card className="min-w-0 lg:col-span-2">
                <h2 className="text-sm font-semibold uppercase tracking-wide text-ga-text-muted">
                  Current Active Tournament
                </h2>
                {home?.active_tournament ? (
                  <div className="mt-4">
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div>
                        <Link
                          to={`/dashboard/tournaments/${home.active_tournament.id}`}
                          className="text-lg font-bold text-ga-text-primary hover:text-ga-primary"
                        >
                          {home.active_tournament.name}
                        </Link>
                        <p className="mt-1 text-sm text-ga-text-muted">
                          {home.active_tournament.facility_name}
                        </p>
                      </div>
                      <StatusPill {...statusPill(home.active_tournament.lifecycle_status)} />
                    </div>
                    <div className="mt-5 grid gap-3 sm:grid-cols-3">
                      <div className="rounded-ga-sm bg-ga-bg-surface-subtle p-3">
                        <p className="text-xs text-ga-text-muted">Teams</p>
                        <p className="mt-1 font-bold text-ga-text-primary">
                          {home.active_tournament.team_count || 0}
                          {home.active_tournament.max_teams ? ` / ${home.active_tournament.max_teams}` : ''}
                        </p>
                      </div>
                      <div className="rounded-ga-sm bg-ga-bg-surface-subtle p-3">
                        <p className="text-xs text-ga-text-muted">Entry fee</p>
                        <p className="mt-1 font-bold text-ga-text-primary">
                          ₹{Number(home.active_tournament.registration_amount || 0).toLocaleString()}
                        </p>
                      </div>
                      <div className="rounded-ga-sm bg-ga-bg-surface-subtle p-3">
                        <p className="text-xs text-ga-text-muted">Teams paid</p>
                        <p className="mt-1 font-bold text-ga-text-primary">
                          {home.active_tournament.paid_team_count || 0} / {home.active_tournament.team_count || 0}
                        </p>
                      </div>
                    </div>
                  </div>
                ) : (
                  <p className="mt-4 text-sm text-ga-text-muted">No tournament is currently ongoing.</p>
                )}
              </Card>

              <Card className="min-w-0">
                <div className="flex items-center gap-2">
                  <AlertCircle className="h-5 w-5 text-ga-warning" />
                  <h2 className="font-bold text-ga-text-primary">Action Required</h2>
                </div>
                <p className="mt-1 text-sm text-ga-text-muted">
                  {home?.summary?.pending_registrations || 0} pending team registrations
                </p>
                <div className="mt-4 space-y-2">
                  {(home?.action_required || []).length === 0 ? (
                    <p className="text-sm text-ga-text-muted">You are all caught up.</p>
                  ) : home.action_required.map((action) => (
                    <Link
                      key={action.tournament_id}
                      to={`/dashboard/tournaments/${action.tournament_id}?tab=registrations`}
                      className="flex items-center justify-between gap-3 rounded-ga-sm border border-ga-border-subtle px-3 py-2 text-sm hover:bg-ga-bg-surface-subtle"
                    >
                      <span className="min-w-0">
                        <span className="block truncate font-semibold text-ga-text-primary">
                          {action.tournament_name}
                        </span>
                        <span className="text-xs text-ga-text-muted">
                          {action.pending_registrations} pending
                        </span>
                      </span>
                      <ArrowRight className="h-4 w-4 shrink-0 text-ga-primary" />
                    </Link>
                  ))}
                </div>
              </Card>
            </div>

            <Card>
              <div className="flex flex-wrap items-end justify-between gap-3">
                <div>
                  <h2 className="text-lg font-bold text-ga-text-primary">All Tournaments</h2>
                  <p className="text-sm text-ga-text-muted">Open a tournament to manage its details.</p>
                </div>
                <span className="text-sm text-ga-text-muted">{tournaments.length} shown</span>
              </div>

              <div className="mt-4 grid gap-3 md:grid-cols-[minmax(0,1fr)_180px_180px]">
                <label>
                  <span className="mb-1 block text-xs font-semibold text-ga-text-muted">Search</span>
                  <span className="relative block">
                    <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ga-text-subtle" />
                    <input
                      className="ga-input w-full pl-9"
                      value={search}
                      onChange={(event) => setSearch(event.target.value)}
                      placeholder="Tournament or venue"
                    />
                  </span>
                </label>
                <label>
                  <span className="mb-1 block text-xs font-semibold text-ga-text-muted">Status</span>
                  <select className="ga-input w-full" value={status} onChange={(event) => setStatus(event.target.value)}>
                    <option value="all">All</option>
                    <option value="ongoing">Ongoing</option>
                    <option value="upcoming">Upcoming</option>
                    <option value="completed">Completed</option>
                  </select>
                </label>
                <label>
                  <span className="mb-1 block text-xs font-semibold text-ga-text-muted">Sort</span>
                  <select className="ga-input w-full" value={sort} onChange={(event) => setSort(event.target.value)}>
                    <option value="newest">Newest first</option>
                    <option value="start">Start date</option>
                    <option value="name">Name</option>
                  </select>
                </label>
              </div>

              {tournaments.length === 0 ? (
                <EmptyState
                  icon={Trophy}
                  title={home?.tournaments?.length ? 'No tournaments match' : 'No tournaments yet'}
                  description={home?.tournaments?.length
                    ? 'Try changing your search or status filter.'
                    : 'Register your first tournament to get started.'}
                  className="mt-5"
                />
              ) : (
                <div className="mt-5 divide-y divide-ga-border-subtle">
                  {tournaments.map((tournament) => (
                    <Link
                      key={tournament.id}
                      to={`/dashboard/tournaments/${tournament.id}`}
                      className="grid gap-3 py-4 transition-colors hover:bg-ga-bg-surface-subtle sm:grid-cols-[minmax(0,1fr)_auto] sm:px-2"
                    >
                      <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                          <h3 className="truncate font-bold text-ga-text-primary">{tournament.name}</h3>
                          <StatusPill {...statusPill(tournament.lifecycle_status)} size="sm" />
                        </div>
                        <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-ga-text-muted">
                          <span className="inline-flex items-center gap-1">
                            <Calendar className="h-3.5 w-3.5" />
                            {formatDate(tournament.tournament_starts_at)}
                          </span>
                          <span className="inline-flex items-center gap-1">
                            <Users className="h-3.5 w-3.5" />
                            {tournament.team_count || 0} teams
                          </span>
                          <span className="inline-flex items-center gap-1">
                            <IndianRupee className="h-3.5 w-3.5" />
                            {Number(tournament.registration_amount || 0).toLocaleString()}
                          </span>
                        </div>
                      </div>
                      <span className="inline-flex items-center gap-1 self-center text-sm font-semibold text-ga-primary">
                        View <ArrowRight className="h-4 w-4" />
                      </span>
                    </Link>
                  ))}
                </div>
              )}
            </Card>
          </>
        )}

      </div>
    </DashboardLayout>
  );
}
